<template>
  <div class="Question" id="questions">
    <div @click="showAnswer" class="QuestionContent">
      <div class="QuestionTitle">
        <span class="QuestionNumber">{{ number }}.</span>
        <h1 v-html="title"></h1>
      </div>
      <div class="QuestionAttributes">
        <div class="QuestionLocked" :class="[isLocked ? 'active' : '']">
          <img src="../assets/img/lock.svg" alt="premium question" />
        </div>
        <div class="QuestionDifficulty">
          <DifficultyLabel :difficulty="question.difficulty" />
        </div>
        <CompletedLabel :completed="isCompleted" :uid="question.uid" />
      </div>
    </div>
    <div class="Answer AnswerWrapper" :class="{ opened: isOpened }">
      <div class="AnswerLabel">
        <h1 class="AnswerTitle">Answer:</h1>
        <div class="AnswerDivider"></div>
      </div>
      <p v-html="answerContent" class="AnswerText"></p>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import DifficultyLabel from './DifficultyLabel';
import CompletedLabel from './CompletedLabel';

export default {
  name: 'Question',
  props: {
    question: Object,
    number: Number,
  },
  components: {
    DifficultyLabel,
    CompletedLabel,
  },
  data() {
    return {
      isOpened: false,
      answerContent: '',
    };
  },
  methods: {
    ...mapActions(['fetchAnswer', 'updateIsModalOpened']),
    async showAnswer() {
      if (!this.question.isFree && !this.isSignedIn) {
        this.updateIsModalOpened(true);
        return;
      } else {
        if (this.answerContent) {
          this.isOpened = !this.isOpened;
        } else {
          const answer = await this.fetchAnswer(this.question.uid);
          this.answerContent = answer.content;
          this.isOpened = !this.isOpened;
        }
      }
    },
  },
  computed: {
    ...mapGetters(['getCompletedList', 'isSignedIn']),
    title() {
      return this.question.title;
    },
    isCompleted() {
      return this.getCompletedList.includes(this.question.uid);
    },
    isLocked() {
      if (this.isSignedIn) return false;
      return !this.question.isFree;
    },
  },
};
</script>

<style lang="scss">
.QuestionContent {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  border-radius: 48px;
  cursor: pointer;
  &:hover {
    background-color: $color-background-light;
    transition: all 0.3s;
  }
}
.QuestionTitle {
  display: flex;
  justify-content: space-between;
  align-items: center;
  h1 {
    align-items: center;
    font-weight: 600;
    font-size: 18px;
    color: $color-black;
    line-height: 1.3;
    code {
      margin: 0 5px;
      font-size: 17px;
      font-weight: 600;
    }
  }
  .QuestionNumber {
    font-weight: 400;
    font-size: 18px;
    margin-right: 10px;
  }
}
.QuestionAttributes {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.QuestionLocked {
  display: none;
  width: 25px;
  height: 25px;
  margin-right: 10px;
  &.active {
    display: block;
  }
}

.Answer {
  width: 750px;
  margin-left: 40px;
  padding: 17px 0;
}

.AnswerWrapper {
  overflow: hidden;
  max-height: 0;
  transition: max-height 0.4s cubic-bezier(0, 1, 0, 1);
  transition: padding 0.2s;
  padding: 0px 0;
}

.opened {
  max-height: 100%;
  transition: max-height 0.4s ease-in-out;
  transition: padding 0.2s;
  padding: 17px 0;
}

.AnswerLabel {
  display: inline-block;
  margin-bottom: 15px;
}
.AnswerTitle {
  font-weight: 600;
  font-size: 18px;
  color: $color-black;
}
.AnswerDivider {
  width: 100%;
  height: 4px;
  background: $color-purple;
  border-radius: 30px;
}

.AnswerText {
  p {
    font-size: 16px;
    color: #46454a;
    margin: 10px 0;
    line-height: 1.5;
  }
  strong {
    color: #5b45e0;
    font-weight: 500;
  }
  ul {
    padding: 15px 0;
    display: block;
    margin-left: 20px;
    color: $color-grey;
    font-size: 16px;
  }
  ol {
    padding: 15px 0;
    display: block;
    margin-left: 20px;
    color: $color-grey;
    font-size: 16px;
  }
  li {
    margin-bottom: 15px;
    font-size: 16px;
  }
  img {
    width: 80%;
    padding: 30px 0;
  }
  hr {
    opacity: 0;
    margin: 10px 0;
  }
  .text-muted {
    font-size: 14px;
    color: #6c757d;
    a {
      color: #6c757d;
      text-decoration: none;
      font-style: italic;
    }
  }
}

code,
pre {
  color: #f8f8f2;
  background: none;
  text-shadow: 0 1px rgba(0, 0, 0, 0.3);
  font-family: 'Courier New', Courier, monospace;
  font-size: 16px;
  text-align: left;
  white-space: pre;
  word-spacing: normal;
  word-break: normal;
  word-wrap: normal;
  line-height: 1.5;

  -moz-tab-size: 4;
  -o-tab-size: 4;
  tab-size: 4;

  -webkit-hyphens: none;
  -moz-hyphens: none;
  -ms-hyphens: none;
  hyphens: none;

  background: #272822;
}
p {
  em {
    color: #5b45e0;
    font-weight: 500;
    font-style: normal;
  }
}
pre {
  padding: 16px;
  margin: 0.5em 0;
  overflow: auto;
  border-radius: 0.3em;
}

:not(pre) > code {
  font-family: $font-primary;
  font-weight: 500;
  background: none;
  text-shadow: none;
  color: #5b45e0;
}

@media screen and (max-width: 1200px) {
  .QuestionTitle {
    width: 80%;
    justify-content: flex-start;
  }
  .AnswerWrapper {
    width: 80%;
  }
}

@media screen and (max-width: 650px) {
  .Questions {
    border-radius: 20px;
  }
  .QuestionContent {
    padding: 10px;
  }
  .QuestionTitle {
    h1 {
      font-size: 15px;
      font-weight: 500;
    }
  }
  .QuestionLocked {
    width: 22px;
    height: 22px;
    margin-right: 0;
  }

  .Answer {
    margin-left: 10px;
  }

  .AnswerWrapper {
    width: 95%;
  }

  .AnswerText {
    p {
      font-size: 15px;
    }
    code {
      font-size: 14px;
    }
  }
}
</style>
